import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { StoryArc } from '@/components/story/arc-section'

import { pick, toLocale, useLocale } from '@/hooks/use-locale'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import ArcSection from '@/components/story/arc-section'
import StoryCta from '@/components/story/story-cta'
import StoryHero from '@/components/story/story-hero'

interface StoryPageProps {
    arcs: StoryArc[]
}

const StoryPage: FC<StoryPageProps> = ({ arcs }): ReactNode => {
    const { t } = useLocale()

    return (
        <Layout headerTone='dark'>
            <Seo title={t.storyTitle} description={t.storyDescription} />

            <StoryHero arcs={arcs} />

            {arcs.map((arc, index) => (
                <ArcSection
                    key={arc.id}
                    arc={arc}
                    tinted={index % 2 === 1}
                    divided={index > 0}
                />
            ))}

            <StoryCta />
        </Layout>
    )
}

const toImage = (value?: string): string | null => {
    const src = typeof value === 'string' ? value.trim() : ''
    return src || null
}

export const getStaticProps: GetStaticProps<StoryPageProps> = async ({ locale }) => {
    const lang = toLocale(locale)

    const [{ arcs }, { parvas }] = await Promise.all([
        import('@/data/arcs'),
        import('@/data/parvas')
    ])

    const stories = [...arcs]
        .sort((a, b) => a.number - b.number)
        .map((arc): StoryArc => {
            const [from, to] = arc.range

            return {
                id: arc.id,
                number: arc.number,
                range: [from, to],
                title: pick(arc.title, lang),
                intro: pick(arc.intro, lang),
                image: toImage(arc.image),
                imageAlt: pick(arc.imageAlt, lang),
                events: arc.events
                    .map(event => ({
                        parva: event.parva,
                        title: pick(event.title, lang),
                        body: pick(event.body, lang)
                    }))
                    .filter(event => event.title || event.body),
                chapters: parvas
                    .filter(parva => parva.number >= from && parva.number <= to)
                    .map(parva => ({
                        number: parva.number,
                        title: pick(parva.title, lang),
                        headline: pick(parva.headline, lang)
                    }))
            }
        })

    return {
        props: {
            arcs: stories
        }
    }
}

export default StoryPage
